import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import NetworkErrorMessage from '@messenger/components/channel/message/NetworkErrorMessage';
import { SendbirdContextProps } from '@messenger/constants';
import { SENDBIRD_APP_ID } from '~constants/sendbird';
import {
  SendbirdProvider as CoreSendbirdProvider,
  useSendbirdCore,
  getSbInstance,
  useGroupChannelCollection,
  useChannelEnrichment,
  type MyChannelResponseData,
} from '@myrealtrip/messenger-core';
import {
  type GroupChannel,
  GroupChannelListOrder,
  type SendbirdGroupChat,
} from '@sendbird/chat/groupChannel';
import { FileMessage, UserMessage } from '@sendbird/chat/message';
import { useRouter } from 'next/router';

import { fetchChannelEnrichmentsQuery } from '~queries/messenger/useChannelEnrichments';

// 채팅 목록·채팅방 진입 시에만 foreground 로 전환하여 사용자가 online 으로 표시되게 한다.
// 그 외 페이지(bridge, promotion, error 등)에서는 background 로 두어 connect 는 유지하되
// 다른 사용자에게 online 으로 노출되지 않도록 한다 (legacy 동치).
const FOREGROUND_PATHS = ['/channels', '/channels/[channelId]'];

const noop = () => null;

/**
 * Traveler Enrichment 타입
 */
export interface TravelerEnrichment {
  messengerGroupType: MyChannelResponseData['messengerGroupType'];
  hasUnreadMessage: boolean;
  coverUrl: string;
}

/**
 * Frontend 전용 Context 타입
 */
interface FrontendSendbirdContextProps extends SendbirdContextProps {
  collectionChannels: GroupChannel[];
  hasMoreChannels: boolean;
  loadMoreChannels: () => Promise<void>;
  isChannelsLoading: boolean;
  enrichmentMap: Map<string, TravelerEnrichment>;
  isEnrichmentLoading: boolean;
}

export const SendbirdContext = createContext<FrontendSendbirdContextProps>({
  sendbirdUserId: '',
  sbInstance: null,
  channels: null,
  setChannels: () => null,
  isAlone: false,
  setIsAlone: () => null,
  messages: null,
  setMessages: () => null,
  mediaViewer: { isOpen: false },
  setMediaViewer: () => null,
  collectionChannels: [],
  hasMoreChannels: false,
  loadMoreChannels: async () => {},
  isChannelsLoading: false,
  enrichmentMap: new Map(),
  isEnrichmentLoading: false,
});

interface Props {
  userId?: number;
  accessToken?: string;
  channelId?: string;
  children: React.ReactNode;
}

const SendbirdProvider = ({ userId, accessToken, channelId, children }: Props) => {
  const sendbirdUserId = Number.isFinite(userId) ? String(userId) : '';

  return (
    <CoreSendbirdProvider
      appId={SENDBIRD_APP_ID}
      userId={sendbirdUserId}
      accessToken={accessToken}
      channelId={channelId}
    >
      <PresenceController>
        <FrontendContextBridge sendbirdUserId={sendbirdUserId}>{children}</FrontendContextBridge>
      </PresenceController>
    </CoreSendbirdProvider>
  );
};

/**
 * 라우트 변화에 따라 Sendbird 의 foreground/background 상태를 토글한다.
 * connect 는 SendbirdProvider 가 유지하고, 채팅 페이지에 한해 foreground 로 노출한다.
 * pagehide(WebView 종료/탭 닫기 등) 시에도 background 로 전환하여 사용자가 다른 사용자에게
 * online 으로 잘못 노출되지 않게 한다.
 */
const PresenceController = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const { currentUser, enterForeground, enterBackground } = useSendbirdCore();

  useEffect(() => {
    if (!currentUser) return;
    if (FOREGROUND_PATHS.includes(router.pathname)) {
      enterForeground();
    } else {
      enterBackground();
    }
  }, [router.pathname, currentUser, enterForeground, enterBackground]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePageHide = () => {
      if (currentUser) enterBackground();
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [currentUser, enterBackground]);

  return <>{children}</>;
};

/**
 * Core Context → Frontend Context 브릿지
 * Collection + Enrichment를 Provider에서 관리 (라우트 전환 시 유지)
 */
const FrontendContextBridge = ({
  sendbirdUserId,
  children,
}: {
  sendbirdUserId: string;
  children: React.ReactNode;
}) => {
  const core = useSendbirdCore();

  // 백그라운드/포어그라운드 전환은 SDK의 appStateToggleEnabled(기본 true)에 위임한다.
  // 직접 setBackgroundState/Foreground를 호출하면 hidden 즉시 WebSocket이 끊겨,
  // 모바일 웹에서 파일 picker로 잠깐 화면을 가린 사이 파일 첨부가 실패하던 회귀가 발생했다.

  // ─── Collection ───
  const sdkInstance = core.currentUser ? (getSbInstance() as SendbirdGroupChat) : null;
  const {
    channels: collectionChannels,
    hasMore: hasMoreChannels,
    loadMore: loadMoreChannels,
    isLoading: isChannelsLoading,
  } = useGroupChannelCollection({
    sdk: sdkInstance,
    filter: { includeEmpty: false },
    order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
    limit: 20,
  });

  // ─── Enrichment (Provider에서 관리 → 라우트 전환 시 유지) ───
  const fetchEnrichment = useCallback(
    async (urls: string[]): Promise<Record<string, TravelerEnrichment>> => {
      try {
        const enrichments = await fetchChannelEnrichmentsQuery(urls);
        const result: Record<string, TravelerEnrichment> = {};
        enrichments.forEach((ch) => {
          result[ch.channelUrl] = {
            messengerGroupType: ch.messengerGroupType as TravelerEnrichment['messengerGroupType'],
            hasUnreadMessage: false,
            coverUrl: ch.coverUrl ?? '',
          };
        });
        return result;
      } catch (error) {
        console.error('[Enrichment] Failed to fetch channel enrichments:', error);
        return {};
      }
    },
    [],
  );

  const channelUrls = useMemo(() => collectionChannels.map((ch) => ch.url), [collectionChannels]);

  const { enrichmentMap, isLoading: isEnrichmentLoading } =
    useChannelEnrichment<TravelerEnrichment>({
      channelUrls,
      fetchEnrichment,
      enabled: channelUrls.length > 0,
    });

  const contextValues = useMemo<FrontendSendbirdContextProps>(
    () => ({
      sbInstance: core.currentUser,
      sendbirdUserId,
      channels: null,
      setChannels: noop as any,
      currentChannel: core.currentChannel,
      isAlone: core.isAlone,
      setIsAlone: core.setIsAlone,
      messages: core.messages,
      setMessages: noop as any,
      mediaViewer: core.mediaViewer,
      setMediaViewer: core.setMediaViewer,
      collectionChannels,
      hasMoreChannels,
      loadMoreChannels,
      isChannelsLoading,
      enrichmentMap,
      isEnrichmentLoading,
    }),
    [
      core.currentUser,
      sendbirdUserId,
      core.currentChannel,
      core.isAlone,
      core.setIsAlone,
      core.messages,
      core.mediaViewer,
      core.setMediaViewer,
      collectionChannels,
      hasMoreChannels,
      loadMoreChannels,
      isChannelsLoading,
      enrichmentMap,
      isEnrichmentLoading,
    ],
  );

  return <SendbirdContext.Provider value={contextValues}>{children}</SendbirdContext.Provider>;
};

const useSendbird = () => {
  const core = useSendbirdCore();
  const {
    collectionChannels,
    hasMoreChannels,
    loadMoreChannels,
    isChannelsLoading,
    enrichmentMap,
    isEnrichmentLoading,
  } = useContext(SendbirdContext);

  const getMessageStatus = useCallback(
    (message: UserMessage | FileMessage) => {
      if (!core.currentChannel) return;
      const { memberCount } = core.currentChannel;
      const { sendingStatus } = message;
      if (!memberCount) return;
      if (['pending', 'failed'].includes(sendingStatus ?? '')) {
        return <NetworkErrorMessage sendingStatus={sendingStatus} />;
      }
      return '';
    },
    [core.currentChannel],
  );

  return {
    ...core,
    collectionChannels,
    hasMoreChannels,
    loadMoreChannels,
    isChannelsLoading,
    enrichmentMap,
    isEnrichmentLoading,
    getMessageStatus,
  };
};

export { SendbirdProvider, useSendbird };
